import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { Box, Button } from '@mui/material';
import classnames from 'classnames';
import _ from 'lodash';
import { toast, useToasterStore } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import auth from '../../../components/auth';
import { CommunityPostCell } from './CommunityPostCell';
import { getReplyInDMHandler } from '../utils/replyInDM';
import { useCommunityPosts } from '../hooks/useCommunityPosts';
import { PostSkeleton } from '../components/PostSkeleton';
import { LoadMorePosts } from '../components/LoadMorePosts';
import AddPost from '../components/AddPost';
import { useDeleteCommunityPost } from '../hooks/useDeleteCommunityPost';
import { useToggleReactionPost } from '../hooks/useToggleReactionPost';
import { useTogglePinPost } from '../hooks/useTogglePinPost';
import {
  Post,
  ReactionType,
  useBlockUserMutation,
} from '../../../../generated/graphql';
import { useCommunityIndicator } from '../../hooks/use-community-indicator';
import { communityQueryKeys, MAX_PINNED_POSTS } from '../constants';
import { EmptyFeed } from './EmptyFeed';
import { trackEvent } from '@services/tracking';
import { PinnedPostsSection } from './PinnedPostsSection';
import { usePinnedPosts } from '../hooks/usePinnedPosts';
import {
  updateReactionOnEntity,
  updateReactionOnList,
} from '../utils/toggleReactionHelpers';

type UserData = {
  name: string;
  picture: string;
  isOwner: boolean;
};
type Props = {
  influencerId: number;
  communityId: number;
  user: UserData;
  isDmFeatureEnabled: boolean;
};

export const CommunityFeed = ({
  influencerId,
  communityId,
  user,
  isDmFeatureEnabled,
}: Props) => {
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const queryClient = useQueryClient();
  const deletePostMutation = useDeleteCommunityPost(communityId);
  const togglePostReaction = useToggleReactionPost(communityId, {
    skipOptimistic: true,
  });
  const togglePinPost = useTogglePinPost(communityId);
  const { data: pinnedPosts } = usePinnedPosts(communityId);
  const [blockUserMutation] = useBlockUserMutation();
  const [hasNewPosts, setHasNewPosts] = useState<boolean>(false);
  const [addPostVisible, setAddPostVisible] = useState<boolean>(true);
  const addPostRef = useRef<HTMLDivElement>(null);
  const lastSeenTopPostIdRef = useRef<string | null>(null);
  const lastToastHandledRef = useRef<string | null>(null);
  const { toasts } = useToasterStore();

  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(
    null,
  );

  const pinnedPostIds = new Set(pinnedPosts?.map((p) => p.id) ?? []);
  const pinCount = pinnedPosts?.length ?? 0;
  const pinLimitReached = pinCount >= MAX_PINNED_POSTS;

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isInitialLoading,
    hasNextPage,
  } = useCommunityPosts(communityId);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAddPostVisible(entry.isIntersecting);
      },
      { rootMargin: '-50px' },
    );

    if (addPostRef.current) {
      observer.observe(addPostRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasNewPosts]);

  const flattenedPosts = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page ?? []),
    [data],
  );

  useEffect(() => {
    if (_.isEmpty(flattenedPosts)) {
      return;
    }

    const topPostId = flattenedPosts[0]?.id ?? null;
    if (lastSeenTopPostIdRef.current === null) {
      lastSeenTopPostIdRef.current = topPostId;
      return;
    }

    setHasNewPosts(topPostId !== lastSeenTopPostIdRef.current);
  }, [flattenedPosts]);

  useEffect(() => {
    const posts = data?.pages[0] ?? [];
    const includesConsumerPost = posts.some(
      (post) => post.author.id !== influencerId,
    );
    const includesCreatorPost = posts.some(
      (post) => post.author.id === influencerId,
    );

    trackEvent({
      type: 'Community Viewed',
      payload: {
        'Includes a consumer post': String(includesConsumerPost),
        'Includes a creator post': String(includesCreatorPost),
      },
    });
  }, [data]);

  const { setLastSeenPostId } = useCommunityIndicator(true);
  if (!isAdminModerator && data?.pages && !_.isEmpty(data.pages)) {
    const page = data.pages[0];
    page && !_.isEmpty(page) && setLastSeenPostId(page[0].id);
  }

  const onBlock = async (communityId: number, userId: number) => {
    return blockUserMutation({
      variables: {
        communityId,
        userId,
      },
    })
      .then(() => {
        toast.success('User blocked successfully');
        queryClient.refetchQueries(communityQueryKeys.posts(+communityId));
      })
      .catch(() => {
        toast.error('Something went wrong with blocking user');
      });
  };

  const onLoadNewPosts = useCallback(() => {
    if (!_.isEmpty(flattenedPosts)) {
      lastSeenTopPostIdRef.current = flattenedPosts[0]?.id ?? null;
    }
    setHasNewPosts(false);
    scrollToTop();
  }, [flattenedPosts]);

  useEffect(() => {
    if (!hasNewPosts) return;

    const activeToast = toasts.find((toastItem) => toastItem.visible);

    if (!lastToastHandledRef.current && activeToast) {
      lastToastHandledRef.current = activeToast.id;
    }

    if (!lastToastHandledRef.current) {
      return;
    }

    const trackedToast = toasts.find(
      (toastItem) => toastItem.id === lastToastHandledRef.current,
    );

    if (!trackedToast) {
      const timer = setTimeout(() => {
        onLoadNewPosts();
        lastToastHandledRef.current = null;
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (trackedToast.visible) {
      return;
    }

    const timer = setTimeout(() => {
      onLoadNewPosts();
      lastToastHandledRef.current = null;
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasNewPosts, toasts, onLoadNewPosts]);

  const scrollToTop = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  const handleToggleReaction = async (postId: string, type: ReactionType) => {
    const listKey = communityQueryKeys.posts(communityId);
    const postKey = communityQueryKeys.post(communityId, postId);
    const pinnedKey = communityQueryKeys.pinnedPosts(communityId);

    const previousList = queryClient.getQueryData(listKey);
    const previousPost = queryClient.getQueryData(postKey);
    const previousPinned = queryClient.getQueryData(pinnedKey);

    updateReactionOnList({
      entityId: postId,
      queryClient,
      queryKey: listKey,
      type,
    });
    updateReactionOnEntity({
      entityId: postId,
      queryClient,
      queryKey: postKey,
      type,
    });
    updateReactionOnList({
      entityId: postId,
      queryClient,
      queryKey: pinnedKey,
      type,
    });

    try {
      await togglePostReaction.mutateAsync({ type, postId });
    } catch (error) {
      queryClient.setQueryData(listKey, () => previousList);
      queryClient.setQueryData(postKey, () => previousPost);
      queryClient.setQueryData(pinnedKey, () => previousPinned);
      toast.error('Something went wrong');
    }
  };

  const handleGoToPost = (postId: string) => {
    const element = document.getElementById(`post-${postId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedPostId(postId);
      setTimeout(() => setHighlightedPostId(null), 2000);
    }
  };

  const hasPosts = flattenedPosts.length > 0;

  return (
    <Box>
      {/* Pinned Posts - outside white container */}
      <PinnedPostsSection
        communityId={communityId}
        influencerId={influencerId}
        isUserCommunityOwner={user.isOwner}
        isDmFeatureEnabled={isDmFeatureEnabled}
        onUnpin={(postId, post) =>
          togglePinPost.mutate({ postId, pinned: false, post })
        }
        onBlock={onBlock}
        onToggleReaction={(postId, type) => handleToggleReaction(postId, type)}
        onDelete={(postId) => deletePostMutation.mutate(postId)}
        onGoToPost={handleGoToPost}
      />
      <Box className="bg-white md:p-[30px] rounded-2xl">
        <Box ref={addPostRef}>
          <AddPost
            communityId={communityId}
            ownerProfilePicture={user.picture}
            ownerName={user.name}
          />
        </Box>
        {isInitialLoading ? (
          <PostSkeleton />
        ) : (
          data &&
          hasPosts && (
            <Box className="relative">
              <Box
                className={classnames(
                  'w-full flex justify-between z-10000 top-2.5',
                  {
                    absolute: addPostVisible,
                    'fixed right-4 lg:pl-[280px]': !addPostVisible,
                  },
                )}
              >
                {!addPostVisible && <Box className="w-[100px]" />}
                {hasNewPosts && (
                  <Button
                    variant="contained"
                    className={classnames(
                      'rounded-[100px] w-[145px] h-[28px] m-auto',
                    )}
                    startIcon={
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className="pl-2.5 h-4"
                      />
                    }
                    onClick={onLoadNewPosts}
                  >
                    <span className="px-2">New Posts</span>
                  </Button>
                )}
                {!addPostVisible && (
                  <Button
                    variant="contained"
                    className="rounded-[100px] w-[80px] h-[28px]"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className="pl-2.5 h-4"
                      />
                    }
                    onClick={scrollToTop}
                  >
                    Top
                  </Button>
                )}
              </Box>
              {flattenedPosts.map((post) => {
                const isPinned = pinnedPostIds.has(post.id);
                return (
                  <CommunityPostCell
                    influencerId={influencerId}
                    communityId={communityId}
                    isUserCommunityOwner={user.isOwner}
                    postData={post}
                    key={post.id}
                    onDelete={() => deletePostMutation.mutate(post.id)}
                    onBlock={onBlock}
                    onToggleReaction={(type) =>
                      handleToggleReaction(post.id, type)
                    }
                    onReplyInDM={getReplyInDMHandler({
                      author: post.author,
                      isOwner: user.isOwner,
                      isAdminModerator,
                      influencerId,
                      isInfluencer: auth.isInfluencer(),
                      isDmFeatureEnabled,
                    })}
                    isPinned={isPinned}
                    onPin={() =>
                      togglePinPost.mutate({
                        postId: post.id,
                        pinned: !isPinned,
                        post,
                      })
                    }
                    pinLimitReached={pinLimitReached}
                    isHighlighted={highlightedPostId === post.id}
                  />
                );
              })}
            </Box>
          )
        )}
        {!isInitialLoading && hasPosts && hasNextPage && (
          <LoadMorePosts
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
      </Box>
      {!isInitialLoading && !hasPosts && <EmptyFeed />}
    </Box>
  );
};
